/* eslint-disable max-lines*/
import { camelCase, isNil, isString } from 'lodash';

import { DEFAULT_EXTERNAL_VALIDATION_SERVER_ERROR_NOTIFY_THROTTLE_MINUTES } from './appointment-validation';
import { DatabaseEntities } from './base';
import {
  DEFAULT_GEOFENCE_RADIUS_UNIT,
  GeofenceRadiusUnit,
  SELF_CHECKIN_LEAD_TIME_MINUTES,
  SELF_CHECKIN_RADIUS_LIMIT_MILES
} from './checkin';
import { FileUnits } from './file';
import type { Maybe, Nullable, TypeormEntity } from './globals';
import { t } from './i18n';
import { isGridTileLoadTypeCustomFormFieldKey } from './custom-forms';
import { breakWordsAtCaps, sortObjectByKeys } from './js-helpers';
import { GlobalLimits } from './limits';
import { AppointmentStatus } from './status';

export const DEFAULT_APPOINTMENT_START_TIME_INTERVAL = 30;
export const DEFAULT_ID_VALIDATION_DATA_RETENTION_DAYS = 30;
export const ID_VALIDATION_DATA_RETENTION_DAYS_LIMITS = { min: 1, max: 5000 };

export enum AppointmentStartTimeMinutes {
  EVERY_HOUR = 60,
  EVERY_THIRTY_MINUTES = 30,
  EVERY_TWENTY_MINUTES = 20,
  EVERY_FIFTEEN_MINUTES = 15,
  CUSTOM = 'custom'
}

export enum ReferenceNumberInputModes {
  MODE_ALPHANUMERIC = 'alphanumeric',
  MODE_NUMERIC = 'numeric'
}

export const DATE_DISPLAY_FORMATS = [
  'MDY',
  'DMY',
  'YMD',
  'weekdayMDY',
  'weekdayDMY',
  'weekdayYMD'
] as const;
export type DateDisplayFormat = (typeof DATE_DISPLAY_FORMATS)[number];

export const TIME_DISPLAY_FORMATS = ['twelveHour', 'twentyFourHour'] as const;
export type TimeDisplayFormat = (typeof TIME_DISPLAY_FORMATS)[number];

export enum ObservationMatchAttribute {
  LICENSE_PLATE = 'License Plate',
  DOT_NUMBER = 'DOT Number',
  ANY = 'Any',
  /** License plate is required at check-in; DOT number is optional and used as a matching fallback */
  CASCADE = 'Cascade'
}

export enum CheckoutIdentifierAttribute {
  LICENSE_PLATE = 'License Plate',
  PHONE_NUMBER = 'Phone Number',
  ANY = 'Any'
}

/**
 * Hard-coded Persona template IDs for check-in identity verification.
 * These Inquiry IDs are predefined by Persona and will change across all environments.
 * Each template defines the verification workflow and requirements
 */
const CHECK_IN_VALIDATION_INQUIRY_TEMPLATE_OPTIONS: IDropDownOption[] = [
  {
    label: 'Driver license - Front, barcode, face, us only, 1 retry',
    value: 'itmpl_AMnyoPWbmCCgHw7oNkNgvzMbZM2icM'
  },
  {
    label: 'Driver license - Front - 1 retry',
    value: 'itmpl_EZKf7H6NcQL4pbSGW2s5B276D854'
  }
];

export const CHECK_IN_VALIDATION_TEMPLATES: Record<string, IDropDownOption[]> = {
  development: [...CHECK_IN_VALIDATION_INQUIRY_TEMPLATE_OPTIONS],
  staging: [...CHECK_IN_VALIDATION_INQUIRY_TEMPLATE_OPTIONS],
  production: [...CHECK_IN_VALIDATION_INQUIRY_TEMPLATE_OPTIONS],
  qa: [...CHECK_IN_VALIDATION_INQUIRY_TEMPLATE_OPTIONS],
  e2e: [...CHECK_IN_VALIDATION_INQUIRY_TEMPLATE_OPTIONS],
  test: [...CHECK_IN_VALIDATION_INQUIRY_TEMPLATE_OPTIONS]
};

export function getCheckinIdValidationTemplateOptions(): IDropDownOption[] {
  const templates =
    CHECK_IN_VALIDATION_TEMPLATES[process.env.NODE_ENV ?? ''] ??
    CHECK_IN_VALIDATION_INQUIRY_TEMPLATE_OPTIONS;
  return [...templates];
}

export enum SettingGroups {
  REFERENCE_NUMBER = 'Reference Number',
  APPOINTMENT_START_TIMES = 'Appointment Start Times',
  APPOINTMENT_NOTIFICATIONS = 'Appointment Notifications',
  APPOINTMENT_STATUSES = 'Appointment Statuses',
  CUSTOM_APPPOINTMENTS_GRID = 'Custom Appointments Grid',
  CARRIER_VIEW = 'Scheduling Portal',
  DOCK_SELECTION = 'Dock Selection',
  TIME_DISPLAY = 'Time Display',
  GATE_MANAGEMENT = 'Gate Management',
  SMARTGATE = 'SmartGate',
  APPOINTMENT_VALIDATION_V2 = 'Appointment Validation',
  ID_VALIDATION = 'ID Validation',
  HANDLING_UNITS = 'Handling Units',
  LIMITER = 'Limiter',
  OTHER = 'Other'
}

export function getTranslatedSettingGroups(): Record<string, string> {
  return Object.values(SettingGroups).reduce<Record<string, string>>((acc, group) => {
    acc[camelCase(group)] = t(`core.settings.groups.${camelCase(group)}`);
    return acc;
  }, {});
}

export const settingGroupDescriptions = Object.freeze({
  [SettingGroups.REFERENCE_NUMBER]: {
    description:
      "Reference Number is the reference you can request from carriers when they're scheduling an appointment.",
    link: 'https://opendock.zendesk.com/hc/en-us/articles/12988594213779-Using-the-Reference-Number-',
    linkLabel: ''
  },
  [SettingGroups.APPOINTMENT_START_TIMES]: {
    description: 'Determine the time intervals at which appointments will be allowed to start.',
    link: '',
    linkLabel: ''
  },
  [SettingGroups.APPOINTMENT_NOTIFICATIONS]: {
    description: 'Manage all types of appointment notifications for this {{entity}}.',
    link: '',
    linkLabel: ''
  },
  [SettingGroups.CARRIER_VIEW]: {
    description: 'Manage the Scheduling Portal settings for this {{entity}}',
    link: '',
    linkLabel: ''
  },
  [SettingGroups.DOCK_SELECTION]: {
    description: '',
    link: '',
    linkLabel: ''
  },
  [SettingGroups.HANDLING_UNITS]: {
    description:
      'Handling Units is a numeric field for requesting units (e.g. pallets) during appointment scheduling. ' +
      'Add it to your form to also enable the Limiter feature.',
    link: 'http://opendock.zendesk.com/hc/en-us/articles/12988594213779-Using-the-Reference-Number-', // TODO CHANGE!
    linkLabel: ''
  },
  [SettingGroups.LIMITER]: {
    description:
      'The Limiter allows you to set a maximum number of appointments or handling units (e.g. pallets) that can be scheduled per day.',
    link: 'http://opendock.zendesk.com/hc/en-us/articles/12988594213779-Using-the-Reference-Number-',
    linkLabel: ''
  }
});

type SettingInheritanceMode = 'group' | 'field';

interface ISettingGroupConfig {
  inheritanceMode: SettingInheritanceMode;
}

const settingGroupConfig: Partial<Record<SettingGroups, ISettingGroupConfig>> = {
  [SettingGroups.APPOINTMENT_STATUSES]: { inheritanceMode: 'field' }
};

export function getSettingGroupInheritanceMode(group: Maybe<string>): SettingInheritanceMode {
  if (isNil(group) || group === '') {
    return 'group';
  }
  return settingGroupConfig[group as SettingGroups]?.inheritanceMode ?? 'group';
}

export function getTranslatedSettingGroupMetadata(entity: string) {
  const translatedEntityName = t(`core.global.entities.${entity.toLowerCase()}s`);
  return Object.entries(settingGroupDescriptions).reduce(
    (acc, [group, meta]) => {
      acc[group as SettingGroups] = {
        ...meta,
        label: t(`core.settings.groups.${camelCase(group)}`),
        description: meta.description
          ? t(`core.settings.groupDescriptions.${camelCase(meta.description)}`, {
              entity: translatedEntityName
            })
          : ''
      };
      return acc;
    },
    {} as Record<
      SettingGroups,
      {
        label: string;
        description: string;
        link: string;
        linkLabel: string;
      }
    >
  );
}

export interface IHasSettings {
  settings?: Maybe<ISettings>;
}

// These arrays must be in hierarchical order!
export const entitySettingsHierarchy = {
  Org: [],
  Warehouse: [DatabaseEntities.Org],
  Dock: [DatabaseEntities.Warehouse, DatabaseEntities.Org],
  LoadType: [DatabaseEntities.Warehouse, DatabaseEntities.Org]
};

export type EntitySettingsHierarchyKey = keyof typeof entitySettingsHierarchy;

function readNestedEntitySettings(entity: TypeormEntity, relationNameLowercase: string): ISettings {
  const nested = entity[relationNameLowercase];
  if (!nested || typeof nested !== 'object' || Array.isArray(nested)) {
    return {};
  }
  const settingsVal = (nested as { settings?: unknown }).settings;
  if (isNil(settingsVal) || typeof settingsVal !== 'object' || Array.isArray(settingsVal)) {
    return {};
  }
  return settingsVal as ISettings;
}

export function makeEntitySettings(
  entityType: EntitySettingsHierarchyKey,
  entity: Maybe<TypeormEntity>
): ISettings {
  if (isNil(entity)) {
    return {};
  }

  const hierarchy = entitySettingsHierarchy[entityType];
  entity.settings = entity.settings ?? {};
  const settings = entity.settings;

  if (!EntitySettings[entityType]) {
    return {};
  }

  const originalEntitySettings = { ...settings };

  hierarchy.forEach((relation: DatabaseEntities) => {
    Object.keys(EntitySettings[entityType]).forEach(entitySettingKey => {
      // Should this just be lowercase first?
      const relationSettings = readNestedEntitySettings(entity, relation.toLowerCase());

      // Get the keys of the settings that belongs to the same group
      const sameGroupSettings = Object.keys(Settings).filter(
        key => Settings[key].group === Settings[entitySettingKey].group
      );

      const inheritanceMode = getSettingGroupInheritanceMode(Settings[entitySettingKey].group);

      // `group`: block org inherit when any setting in the same group is set on the entity.
      // `field`: inherit each unset key from the parent regardless of siblings in the group.
      const blockParentInheritance =
        inheritanceMode === 'group' &&
        sameGroupSettings.some(key => !isNil(originalEntitySettings[key]));

      if (
        isNil(settings[entitySettingKey]) &&
        !isNil(relationSettings[entitySettingKey]) &&
        !blockParentInheritance
      ) {
        settings[entitySettingKey] = relationSettings[entitySettingKey];
      }
    });
  });

  // Apply default values to missing settings
  Object.keys(EntitySettings[entityType]).forEach(s => {
    if (isNil(settings[s])) {
      settings[s] = EntitySettings[entityType][s].defaultValue;
    }
  });

  return settings;
}

export type CompanySettingsInput = Maybe<Pick<IHasSettings, 'settings'>>;

/**
 * Resolves company date/time display settings with schema defaults for any null or missing keys.
 * Company settings are not inherited from org/warehouse; each key falls back independently.
 *
 * Defaults when `settings` is null, undefined, or partial:
 * - `dateDisplayFormat`: `MDY`
 * - `timeDisplayFormat`: `twelveHour`
 *
 * Does not mutate the input company object.
 */
export function makeCompanySettings(company: CompanySettingsInput): ISettings {
  const settings: ISettings = { ...(company?.settings ?? {}) };

  Object.keys(EntitySettings.Company).forEach(settingKey => {
    if (isNil(settings[settingKey])) {
      settings[settingKey] = EntitySettings.Company[settingKey].defaultValue;
    }
  });

  return settings;
}

export const test = {
  referenceNumberIsVisible: true,
  referenceNumberIsRequired: false,
  appointmentStartTimeMinutes: 30,
  appointmentStartTimeInterval: '30',
  muteAppointmentNotifications: 'thedefaultvalue'
};

export enum SettingInputType {
  String = 'string',
  Date = 'date',
  Bool = 'bool',
  Number = 'int',
  Email = 'email',
  Phone = 'phone',
  DropDown = 'dropdown',
  Document = 'document',
  ClockArray = 'clockArray',
  CheckboxArray = 'checkboxArray',
  LongString = 'longString'
}

export const customSettingInputTypes = [SettingInputType.ClockArray];

export interface IDropDownOption {
  label: string;
  value: string | number;
}

export type SettingsValueType = string | boolean | number | string[];

export interface ISettings {
  [k: string]: Maybe<SettingsValueType>;
}

export interface ISettingsMetadata {
  label?: string; // The human-readable label for the setting
  placeholder?: string;
  /** Includes `null` for optional document / email fields that default to unset. */
  defaultValue: Nullable<SettingsValueType>;
  description?: string; // A short description of the setting
  descriptionEnabled?: string; // A description to be used when the value is truthy, not used if "description" set
  descriptionDisabled?: string; // A description to be used when the value is falsy, not used if "description" set
  inputType: SettingInputType; // The type of input a UI should display for the setting
  valueType: string; // Type for the value, IE Boolean, String, Number, etc
  documentOptions?: {
    aspectRatio: Nullable<number>;
    cropShape: Nullable<'rectangle' | 'circle'>;
    showCropResult: boolean;
    modalHeaderText?: Nullable<string>;
  }; // If the setting is a document, provide options
  dropDownOptions?: Array<IDropDownOption>; // If the setting is a dropdown, provide options
  requirements?: Record<string, unknown>; // Required values of other settings to enable this setting
  overrides?: Record<string, unknown>;
  requiredFields?: Record<string, boolean>; // ENTITY fields that must be set (not null/undf) to enable this setting
  hiddenUntil?: Record<string, unknown>;
  isActive: boolean; // Whether the setting is currently in-use in the system
  group?: string; // A group key for organizing settings in UIs
  maxFileSize?: number; // Limit for doc upload settings
  fileSizeUnit?: FileUnits;
  errorMessage?: string;
  allowedMimeTypes?: string[];
  errorConditions?: Record<string, unknown>;
  booleanLabels?: string[];
  note?: string;
  booleanDropDownOptions?: {
    whenTrue: string;
    exampleTrue?: string;
    whenFalse: string;
    exampleFalse?: string;
  };
  minValue?: number; // Apply to number inputs
  maxValue?: number; // Apply to number inputs
  sectionLabel?: string; // Optional sub-header rendered above this setting within its group
}

export type ISettingsMetadataCollection = {
  [k: string]: ISettingsMetadata;
};

export type ISettingsEntityCollection = {
  [k: string]: {
    [k: string]: ISettingsMetadata;
  };
};

type GroupedSettingsByGroup = Record<string, Record<string, ISettingsMetadata>>;

export function makeGroupedSettings(
  entity: string,
  translateStrings?: false
): GroupedSettingsByGroup {
  // Copied: inactive settings are deleted below, and must not be deleted from EntitySettings itself.
  const settings = translateStrings
    ? getTranslatedSettings(entity)
    : EntitySettings[entity] && { ...EntitySettings[entity] };
  const groupedSettings: GroupedSettingsByGroup = { Other: {} };

  if (!settings) {
    return groupedSettings;
  }
  Object.entries(settings).forEach(([key, setting]) => {
    // Delete (don't return) inactive settings
    if (!setting.isActive) {
      delete settings[key];
    } else {
      if (setting.group && !groupedSettings[setting.group]) {
        groupedSettings[setting.group] = {};
      }

      // Group settings
      if (setting.group) {
        groupedSettings[setting.group][key] = setting;
      } else {
        groupedSettings.Other[key] = setting;
      }
    }
  });

  const sorted = sortObjectByKeys(groupedSettings) satisfies GroupedSettingsByGroup;

  // If there are no "Other" settings, delete the key
  if (!Object.keys(sorted.Other).length) {
    delete sorted.Other;
  } else {
    // Move "Other" to the bottom
    const otherGrp = sorted.Other;
    delete sorted.Other;
    sorted.Other = otherGrp;
  }

  return sorted;
}

export function isSettingHidden(
  _settingKey: string,
  setting: ISettingsMetadata,
  localSettings: ISettings
) {
  let isHidden = false;
  const hiddenUntil = setting.hiddenUntil;
  if (!hiddenUntil) {
    return isHidden;
  }
  Object.keys(hiddenUntil).forEach(key => {
    const condition = hiddenUntil[key];
    if (Array.isArray(condition)) {
      if (!condition.includes(localSettings[key])) {
        isHidden = true;
      }
    } else {
      if (typeof localSettings[key] === 'undefined' || condition !== localSettings[key]) {
        isHidden = true;
      }
    }
  });

  return isHidden;
}

export function shouldDisplayError(
  _settingKey: string,
  setting: ISettingsMetadata,
  localSettings: ISettings
) {
  let shouldDisplayError = false;
  const errorConditions = setting.errorConditions;
  if (!errorConditions) {
    return shouldDisplayError;
  }
  Object.keys(errorConditions).forEach(key => {
    const condition = errorConditions[key];
    if (Array.isArray(condition)) {
      condition.forEach(conditionVal => {
        if (
          conditionVal === localSettings[key] ||
          JSON.stringify(localSettings[key]) === JSON.stringify(conditionVal)
        ) {
          shouldDisplayError = true;
        }
      });
    } else {
      if (condition === localSettings[key]) {
        shouldDisplayError = true;
      }
    }
  });

  return shouldDisplayError;
}

export function areRequiredFieldsSet(
  entity: Record<string, unknown>,
  setting: ISettingsMetadata
): boolean {
  const requirements = setting.requiredFields;
  if (!requirements) {
    return true;
  }

  let allSet = true;

  Object.keys(requirements).forEach(r => {
    if (requirements[r]) {
      if (typeof entity[r] === 'undefined' || entity[r] === null) {
        allSet = false;
      }
    }
  });

  return allSet;
}

export function applyOverrides(settings: ISettings) {
  Object.keys(settings).forEach((settingName: string) => {
    const settingMetaData = Settings[settingName];
    const overrides = settingMetaData?.overrides;

    if (overrides) {
      Object.keys(overrides).forEach(r => {
        if (typeof settings[r] === 'undefined') {
          settings[r] = overrides[r] as SettingsValueType;
        }
      });
    }
  });

  return settings;
}

/**
 * Whether a warehouse update payload should trigger server-side grid tile config normalization
 * (custom appointment fields, template field, or `custom:` / `ltcf:` tile keys).
 */
export function warehouseUpdateNeedsGridTileConfigClean(dto: {
  settings?: ISettings | null;
  customApptFieldsTemplate?: unknown;
}): boolean {
  const config = dto.settings?.gridTileConfig;
  return (
    (Array.isArray(dto.customApptFieldsTemplate) && dto.customApptFieldsTemplate.length > 0) ||
    Object.prototype.hasOwnProperty.call(dto, 'customApptFieldsTemplate') ||
    (Array.isArray(config) &&
      config.some(
        key =>
          isString(key) && (key.startsWith('custom:') || isGridTileLoadTypeCustomFormFieldKey(key))
      ))
  );
}

export function areSettingRequirementsMet(
  settingKey: string,
  setting: ISettingsMetadata,
  localSettings: ISettings
) {
  let met = true;
  const requirements = setting.requirements;
  if (!requirements) {
    return true;
  }
  Object.keys(requirements).forEach(r => {
    const requirement = requirements[r];
    if (Array.isArray(requirement)) {
      if (!requirement.includes(localSettings[r])) {
        met = false;
      }
    } else {
      if (typeof localSettings[r] === 'undefined' || requirement !== localSettings[r]) {
        met = false;
      }
    }

    if (!met) {
      delete localSettings[settingKey];
    }
  });

  return met;
}

const notePrefix = '<strong>Note: </strong>';

// These settings should be considered immutable
// To remove a setting from the application set the "isActive" flag on it to false
export const Settings: ISettingsMetadataCollection = {
  referenceNumbersMinimumCount: {
    label: 'Minimum Amount of Reference Numbers',
    defaultValue: 0,
    description: 'Require at least this amount of reference numbers for an appointment',
    inputType: SettingInputType.Number,
    valueType: 'number',
    requirements: { referenceNumberIsRequired: true },
    isActive: true,
    group: SettingGroups.REFERENCE_NUMBER,
    minValue: 0
  },
  referenceNumbersMaximumCount: {
    label: 'Maximum Amount of Reference Numbers',
    defaultValue: 0,
    description: 'Require at most this amount of reference numbers for an appointment',
    inputType: SettingInputType.Number,
    valueType: 'number',
    isActive: true,
    group: SettingGroups.REFERENCE_NUMBER,
    minValue: 0
  },
  referenceNumberIsVisible: {
    label: 'Include Reference Number field on all appointment forms?',
    defaultValue: true,
    inputType: SettingInputType.Bool,
    valueType: 'boolean',
    booleanDropDownOptions: {
      whenFalse: "No, don't include the Reference Number field",
      whenTrue: 'Yes, include the Reference Number field'
    },
    isActive: true,
    group: SettingGroups.REFERENCE_NUMBER,
    overrides: {
      referenceNumberIsRequired: false // That is, when referenceNumberIsRequired is UNDEFINED, force it being FALSE
    }
  },
  referenceNumberDisplayName: {
    label: 'Reference Number field name',
    defaultValue: 'Reference Number',
    description: 'The display name for the Reference Number field. Eg: "PO Number", "Shipment ID"',
    note: `${notePrefix} If left empty, the default name will be applied`,
    inputType: SettingInputType.String,
    valueType: 'string',
    requirements: { referenceNumberIsVisible: true },
    isActive: true,
    group: SettingGroups.REFERENCE_NUMBER
  },
  referenceNumberHelperText: {
    label: 'Reference Number helper text',
    placeholder: 'Reference Number associated with appointment',
    defaultValue: '',
    description:
      'Additional information to help fill in the Reference Number field.  Eg: "Reference Number should start with OD-XX"',
    note: `${notePrefix} If left empty, the default helper text will be applied`,
    inputType: SettingInputType.String,
    valueType: 'string',
    requirements: { referenceNumberIsVisible: true },
    isActive: true,
    group: SettingGroups.REFERENCE_NUMBER
  },
  referenceNumberIsRequired: {
    label: 'Make Reference Number a required field?',
    defaultValue: false,
    description: 'Require the Reference Number for all appointments being scheduled',
    inputType: SettingInputType.Bool,
    valueType: 'boolean',
    booleanDropDownOptions: {
      whenFalse: "No, don't make Reference Number a required field",
      whenTrue: 'Yes, make Reference Number a required field'
    },
    requirements: { referenceNumberIsVisible: true },
    isActive: true,
    group: SettingGroups.REFERENCE_NUMBER
  },
  referenceNumberIsUnique: {
    label: 'Require Unique Reference Numbers',
    defaultValue: false,
    description: 'Require unique Reference Numbers for all appointments being scheduled',
    note: `${notePrefix} Reference Numbers assigned to cancelled or no-show appointments may be reused on new appointments`,
    inputType: SettingInputType.Bool,
    valueType: 'boolean',
    booleanDropDownOptions: {
      whenFalse: "No, don't require unique Reference Numbers",
      whenTrue: 'Yes, require unique Reference Numbers'
    },
    requirements: { referenceNumberIsVisible: true },
    isActive: true,
    group: SettingGroups.REFERENCE_NUMBER
  },
  referenceNumberInputMode: {
    label: 'Reference Number input mode',
    defaultValue: ReferenceNumberInputModes.MODE_ALPHANUMERIC,
    description: 'The input mode for the Reference Number field',
    inputType: SettingInputType.DropDown,
    valueType: 'string',
    dropDownOptions: [
      {
        label: 'Alphanumeric',
        value: ReferenceNumberInputModes.MODE_ALPHANUMERIC
      },
      {
        label: 'Numeric',
        value: ReferenceNumberInputModes.MODE_NUMERIC
      }
    ],
    isActive: true,
    group: SettingGroups.REFERENCE_NUMBER
  },
  /**
   * @deprecated
   */
  appointmentStartTimeInterval: {
    label: 'Appointment Start Time Interval',
    defaultValue: `${DEFAULT_APPOINTMENT_START_TIME_INTERVAL}`,
    description: 'This is the interval (minutes) between when appointments can start',
    inputType: SettingInputType.DropDown,
    valueType: 'string',
    dropDownOptions: [
      {
        label: '15 Minutes',
        value: '15'
      },
      {
        label: '30 Minutes',
        value: '30'
      },
      {
        label: '60 Minutes',
        value: '60'
      },
      {
        label: '90 Minutes',
        value: '90'
      }
    ],
    isActive: false, // MEANS THIS IS DEPRECATED!
    group: SettingGroups.APPOINTMENT_START_TIMES
  },
  appointmentStartTimeMinutes: {
    label: 'Allow appointments for this {{entity}} to start at:',
    defaultValue: AppointmentStartTimeMinutes.EVERY_THIRTY_MINUTES,
    inputType: SettingInputType.DropDown,
    valueType: 'number | string',
    dropDownOptions: [
      {
        label: 'Top of the hour (:00)',
        value: AppointmentStartTimeMinutes.EVERY_HOUR
      },
      {
        label: 'Top and bottom of the hour (:00 and :30)',
        value: AppointmentStartTimeMinutes.EVERY_THIRTY_MINUTES
      },
      {
        label: 'Every Twenty Minutes (:00, :20, and :40)',
        value: AppointmentStartTimeMinutes.EVERY_TWENTY_MINUTES
      },
      {
        label: 'Every quarter of the hour (:00, :15, :30, and :45)',
        value: AppointmentStartTimeMinutes.EVERY_FIFTEEN_MINUTES
      },
      {
        label: 'Custom start times',
        value: 'custom'
      }
    ],
    isActive: true,
    group: SettingGroups.APPOINTMENT_START_TIMES
  },
  /**
   * TODO: This whole configuration is confusing, we should consider renaming it in the future to "ENABLE appointment
   *  notifications", as the name suggests. The initial idea was indeed "Mute", but we've changed later, which caused
   *  this config to be counter-intuitive.
   */
  muteAppointmentNotifications: {
    label: 'Enable appointment notifications for this {{entity}}?',
    defaultValue: false,
    note: `${notePrefix} Enabling this option does not impact notifications that are already disabled on the appointment level individually.`,
    descriptionEnabled: 'Appointment notifications will be sent',
    descriptionDisabled:
      'Appointment notifications will <strong>NOT</strong> be sent.<br/><strong>Caution:</strong> ALL appointment notifications will be muted for your {{entity}} for ALL users including carriers.',
    inputType: SettingInputType.Bool,
    valueType: 'boolean',
    booleanDropDownOptions: {
      whenFalse: 'No, disable appointment notifications',
      whenTrue: 'Yes, enable appointment notifications'
    },
    isActive: true,
    group: SettingGroups.APPOINTMENT_NOTIFICATIONS
  },
  /**
   * @deprecated
   */
  enableCustomAppointmentStartTimes: {
    label: 'Enable Custom Appointment Start Times',
    defaultValue: false,
    description: 'Enable the Custom Appointment Start Times setting',
    inputType: SettingInputType.Bool,
    valueType: 'boolean',
    isActive: false,
    group: SettingGroups.APPOINTMENT_START_TIMES
  },
  customAppointmentStartTimes: {
    label: 'Select each desired start time individually',
    defaultValue: [],
    inputType: SettingInputType.ClockArray,
    valueType: 'array',
    isActive: true,
    requirements: { appointmentStartTimeMinutes: 'custom' },
    group: SettingGroups.APPOINTMENT_START_TIMES,
    hiddenUntil: { appointmentStartTimeMinutes: 'custom' },
    errorMessage: 'Leaving this field empty will result in no availability on the schedule.',
    errorConditions: { customAppointmentStartTimes: [null, [], undefined] }
  },
  isLoadTypeDurationCarrierViewHidden: {
    label: 'Hide the load type duration?',
    defaultValue: false,
    description:
      'The duration is displayed on the Load Types select list booking an appointment in the Scheduling Portal',
    inputType: SettingInputType.Bool,
    valueType: 'boolean',
    booleanDropDownOptions: {
      whenFalse: "No, don't hide the Load Type duration",
      whenTrue: 'Yes, hide the Load Type duration'
    },
    isActive: true,
    group: SettingGroups.CARRIER_VIEW
  },
  customLogo: {
    label:
      'Upload your {{entity}} logo to display it in the Scheduling Portal and in all email notifications',
    defaultValue: null,
    inputType: SettingInputType.Document,
    valueType: 'document',
    isActive: true,
    group: SettingGroups.CARRIER_VIEW,
    maxFileSize: 800,
    fileSizeUnit: FileUnits.KB,
    documentOptions: {
      aspectRatio: 1,
      cropShape: 'circle',
      showCropResult: true
    },
    allowedMimeTypes: ['image/']
  },
  yardMap: {
    label: 'Upload your warehouse yard map to display it in the Scheduling Portal',
    defaultValue: null,
    inputType: SettingInputType.Document,
    valueType: 'document',
    isActive: true,
    group: SettingGroups.CARRIER_VIEW,
    maxFileSize: 6,
    fileSizeUnit: FileUnits.MB,
    documentOptions: {
      aspectRatio: null,
      cropShape: 'rectangle',
      showCropResult: false,
      modalHeaderText:
        'You can use the zoom feature and drag the image to adjust how the preview will appear for the scheduling users. ' +
        'They will be able  to click the preview to see the full image.'
    },
    allowedMimeTypes: ['image/']
  },
  appointmentCreationStatus: {
    label: 'Status for newly created appointments',
    defaultValue: AppointmentStatus.Scheduled,
    description: 'This only affects newly created appointments',
    inputType: SettingInputType.DropDown,
    valueType: 'string',
    isActive: true,
    group: SettingGroups.APPOINTMENT_STATUSES,
    dropDownOptions: [
      {
        label: AppointmentStatus.Scheduled,
        value: AppointmentStatus.Scheduled
      },
      {
        label: AppointmentStatus.Requested,
        value: AppointmentStatus.Requested
      }
    ]
  },
  revertToRequestedOnCarrierReschedule: {
    label: 'Revert to Requested on carrier reschedule',
    defaultValue: false,
    description:
      'When a carrier reschedules a Scheduled appointment, move it back to Requested for re-approval',
    inputType: SettingInputType.Bool,
    valueType: 'boolean',
    booleanDropDownOptions: {
      whenTrue: 'Revert to Requested on carrier reschedule',
      whenFalse: 'Keep status unchanged on carrier reschedule'
    },
    isActive: true,
    group: SettingGroups.APPOINTMENT_STATUSES,
    requirements: { appointmentCreationStatus: AppointmentStatus.Requested },
    hiddenUntil: { appointmentCreationStatus: AppointmentStatus.Requested }
  },
  mutedAppointmentNotifications: {
    label: 'Mute Appointment Notifications',
    defaultValue: [],
    description: 'A list of muted appointment notifications.',
    inputType: SettingInputType.CheckboxArray,
    valueType: 'array',
    isActive: true,
    group: SettingGroups.APPOINTMENT_NOTIFICATIONS
  },
  allowCarrierDockSelection: {
    label: 'Allow Dock selection in Scheduling Portal?',
    defaultValue: false,
    description: 'Allow Dock selection when booking an appointment in the Scheduling Portal',
    inputType: SettingInputType.Bool,
    valueType: 'boolean',
    booleanDropDownOptions: {
      whenFalse: "No, don't allow Dock selection",
      whenTrue: 'Yes, allow the Dock selection'
    },
    isActive: true,
    group: SettingGroups.DOCK_SELECTION
  },
  statusSucceedingArrived: {
    label: "The status that follows 'Arrived' is",
    defaultValue: AppointmentStatus.Completed,
    description: `This setting will allow the choice of an alternate '${breakWordsAtCaps(
      AppointmentStatus.InProgress
    )}' status between '${AppointmentStatus.Arrived}' and '${AppointmentStatus.Completed}'`,
    inputType: SettingInputType.DropDown,
    valueType: 'string',
    isActive: true,
    group: SettingGroups.APPOINTMENT_STATUSES,
    dropDownOptions: [
      {
        label: breakWordsAtCaps(AppointmentStatus.InProgress),
        value: AppointmentStatus.InProgress
      },
      {
        label: AppointmentStatus.Completed,
        value: AppointmentStatus.Completed
      }
    ]
  },
  enableMilitaryTime: {
    label: 'Display time in:',
    defaultValue: false,
    description:
      'Determine how time will be displayed across the platform for all users of this Org, including the appointments grid and reports.',
    inputType: SettingInputType.Bool,
    valueType: 'boolean',
    isActive: true,
    group: SettingGroups.TIME_DISPLAY,
    booleanDropDownOptions: {
      whenFalse: 'Regular time (12-hour)',
      exampleFalse: 'E.g. 7:00 AM / 7:00 PM',
      whenTrue: 'Military time (24-hour)',
      exampleTrue: 'E.g. 07:00 / 19:00'
    }
  },
  dateDisplayFormat: {
    label: 'Display dates as:',
    defaultValue: 'MDY',
    description:
      'Determine how dates will be displayed for users of this company across company-facing experiences.',
    inputType: SettingInputType.DropDown,
    valueType: 'string',
    dropDownOptions: [
      { label: 'MM/DD/YYYY', value: 'MDY' },
      { label: 'DD/MM/YYYY', value: 'DMY' },
      { label: 'YYYY-MM-DD', value: 'YMD' },
      { label: 'Tue, MM/DD/YYYY', value: 'weekdayMDY' },
      { label: 'Tue, DD/MM/YYYY', value: 'weekdayDMY' },
      { label: 'Tue, YYYY-MM-DD', value: 'weekdayYMD' }
    ],
    isActive: true,
    group: SettingGroups.TIME_DISPLAY
  },
  timeDisplayFormat: {
    label: 'Display time as:',
    defaultValue: 'twelveHour',
    description:
      'Determine how time will be displayed for users of this company across company-facing experiences.',
    inputType: SettingInputType.DropDown,
    valueType: 'string',
    dropDownOptions: [
      { label: '12-hour (e.g. 7:00 AM)', value: 'twelveHour' },
      { label: '24-hour (e.g. 07:00)', value: 'twentyFourHour' }
    ],
    isActive: true,
    group: SettingGroups.TIME_DISPLAY
  },
  useNewGridTiles: {
    label: 'Customize appointment details',
    defaultValue: false,
    description: 'Enable to switch to the new customizable appointment details system',
    inputType: SettingInputType.Bool,
    valueType: 'boolean',
    isActive: true,
    group: SettingGroups.CUSTOM_APPPOINTMENTS_GRID
  },
  gridTileConfig: {
    label: 'Grid tile config',
    defaultValue: [],
    description: 'User configuration of how grid tiles will be displayed.',
    inputType: SettingInputType.CheckboxArray,
    valueType: 'array',
    isActive: true,
    group: SettingGroups.CUSTOM_APPPOINTMENTS_GRID
  },
  gateManagementRequiresGeofencing: {
    label: 'Enforce check-in geofencing to require drivers to provide their real time location',
    defaultValue: false,
    requiredFields: { geolocation: true },
    description:
      'This setting asks for the drivers location and restricts them from checking-in when they are more than one mile away from your warehouse.',
    inputType: SettingInputType.Bool,
    valueType: 'boolean',
    isActive: true,
    group: SettingGroups.GATE_MANAGEMENT
  },
  gateManagementGeofenceRadius: {
    label: 'Geofence Radius',
    defaultValue: SELF_CHECKIN_RADIUS_LIMIT_MILES,
    description:
      'The radius in miles that the driver must be within to check-in. This is only applicable if geofencing is enabled.',
    inputType: SettingInputType.Number,
    valueType: 'number',
    isActive: false,
    group: SettingGroups.GATE_MANAGEMENT,
    minValue: 0
  },
  gateManagementGeofenceRadiusUnit: {
    label: 'Geofence Radius Unit',
    defaultValue: DEFAULT_GEOFENCE_RADIUS_UNIT,
    description: 'The distance unit the geofence radius is measured in.',
    inputType: SettingInputType.DropDown,
    valueType: 'string',
    dropDownOptions: [
      { label: 'Miles', value: GeofenceRadiusUnit.MILES },
      { label: 'Kilometers', value: GeofenceRadiusUnit.KILOMETERS }
    ],
    isActive: false,
    group: SettingGroups.GATE_MANAGEMENT
  },
  gateManagementAdditionalInformationText: {
    label:
      'Would you like to show additional information to the drivers after they check in? (optional)',
    defaultValue: '',
    requiredFields: { geolocation: true },
    inputType: SettingInputType.LongString,
    valueType: 'string',
    isActive: true,
    group: SettingGroups.GATE_MANAGEMENT,
    placeholder: 'Insert message here'
  },
  gateManagementDriverReferenceRequired: {
    label: 'Drivers must enter a reference number to check in',
    defaultValue: false,
    inputType: SettingInputType.Bool,
    valueType: 'boolean',
    isActive: true,
    group: SettingGroups.GATE_MANAGEMENT,
    sectionLabel: 'Drivers'
  },
  gateManagementDriverReferenceValidated: {
    label: 'Drivers can only check in if the reference number matches the scheduled appointment',
    defaultValue: false,
    inputType: SettingInputType.Bool,
    valueType: 'boolean',
    isActive: true,
    requirements: { gateManagementDriverReferenceRequired: true },
    group: SettingGroups.GATE_MANAGEMENT
  },
  gateManagementCustomFirstSmsMessage: {
    label: 'Customize the first SMS message drivers receive after check-in (optional)',
    defaultValue: '',
    description:
      'This message replaces the default check-in confirmation text sent to the driver by SMS. Leave blank to use the default message.',
    inputType: SettingInputType.LongString,
    valueType: 'string',
    isActive: true,
    group: SettingGroups.GATE_MANAGEMENT,
    placeholder: 'Insert message here',
    sectionLabel: 'Check-in SMS'
  },
  gateManagementIncludeYardMapInFirstSms: {
    label: 'Include Yard Map image in the first check-in SMS?',
    defaultValue: false,
    description:
      'Sends your Yard Map as a separate image message right after the check-in SMS. Requires a Yard Map to be uploaded for this warehouse.',
    inputType: SettingInputType.Bool,
    valueType: 'boolean',
    isActive: true,
    group: SettingGroups.GATE_MANAGEMENT,
    sectionLabel: 'Check-in SMS'
  },
  rescheduleByCarrierForbidden: {
    label: 'Prevent rescheduling appointments in the Scheduling Portal?',
    defaultValue: false,
    description: 'Prevent carriers from rescheduling appointments. (optional)',
    inputType: SettingInputType.Bool,
    valueType: 'boolean',
    booleanDropDownOptions: {
      whenFalse: 'Allow rescheduling in the Scheduling Portal ',
      whenTrue: 'Prevent rescheduling in the Scheduling Portal'
    },
    isActive: true,
    group: SettingGroups.CARRIER_VIEW
  },
  observationEventMatchAttribute: {
    label: 'Observation Match Attribute',
    defaultValue: ObservationMatchAttribute.ANY,
    description: 'Attribute to be used for matching observations',
    inputType: SettingInputType.DropDown,
    valueType: 'string',
    dropDownOptions: [
      {
        label: ObservationMatchAttribute.LICENSE_PLATE,
        value: ObservationMatchAttribute.LICENSE_PLATE
      },
      {
        label: ObservationMatchAttribute.DOT_NUMBER,
        value: ObservationMatchAttribute.DOT_NUMBER
      },
      {
        label: ObservationMatchAttribute.ANY,
        value: ObservationMatchAttribute.ANY
      },
      {
        label: ObservationMatchAttribute.CASCADE,
        value: ObservationMatchAttribute.CASCADE
      }
    ],
    isActive: true,
    group: SettingGroups.SMARTGATE
  },
  observationEventMatchTimeWindow: {
    label: 'Observation Match Time Window',
    defaultValue: 5,
    description:
      'The number of minutes to account for an observation before and after the check-in time',
    inputType: SettingInputType.Number,
    valueType: 'number',
    isActive: true,
    group: SettingGroups.SMARTGATE,
    minValue: 1,
    maxValue: 10000
  },
  autoDepartAssets: {
    label: 'Auto Depart Assets',
    description: 'Auto depart assets on receiving outgate event',
    inputType: SettingInputType.Bool,
    valueType: 'boolean',
    defaultValue: false,
    isActive: true,
    group: SettingGroups.SMARTGATE
  },
  checkInEarlyThreshold: {
    label: 'Check-in Early Threshold',
    defaultValue: SELF_CHECKIN_LEAD_TIME_MINUTES,
    description:
      'The number of minutes before the appointment start time that a carrier can check in',
    inputType: SettingInputType.Number,
    valueType: 'number',
    isActive: true,
    group: SettingGroups.GATE_MANAGEMENT,
    minValue: 0
  },
  checkInLateThreshold: {
    label: 'Check-in Late Threshold',
    defaultValue: SELF_CHECKIN_LEAD_TIME_MINUTES,
    description:
      'The number of minutes after the appointment start time that a carrier can check in',
    inputType: SettingInputType.Number,
    valueType: 'number',
    isActive: true,
    group: SettingGroups.GATE_MANAGEMENT,
    minValue: 0
  },
  technicalContactEmailAddress: {
    label: 'Technical Contact Email Address',
    defaultValue: null,
    description: 'Email address where technical issues and information will be sent',
    inputType: SettingInputType.Email,
    valueType: 'string',
    isActive: true,
    note: `${notePrefix} If left empty, no notifications will be sent`,
    group: SettingGroups.APPOINTMENT_VALIDATION_V2
  },
  externalValidationServerErrorNotifyThrottleMinutes: {
    label: 'Technical Contact Email Throttle Minutes',
    defaultValue: DEFAULT_EXTERNAL_VALIDATION_SERVER_ERROR_NOTIFY_THROTTLE_MINUTES,
    description:
      'The time between notifications sent to the technical contact when a validation server error occurs.',
    inputType: SettingInputType.Number,
    valueType: 'number',
    isActive: true,
    group: SettingGroups.APPOINTMENT_VALIDATION_V2,
    minValue: 1,
    maxValue: 120
  },
  overrideExternalAppointmentValidation: {
    label: 'Create Appointment on External Validation Failure',
    defaultValue: false,
    description: 'Create appointments even if appointment validation does not pass.',
    inputType: SettingInputType.Bool,
    valueType: 'boolean',
    isActive: true,
    group: SettingGroups.APPOINTMENT_VALIDATION_V2
  },
  includeHandlingUnitsFieldOnAppointmentForms: {
    label: 'Include Handling Units field on all appointment forms?',
    defaultValue: false,
    inputType: SettingInputType.Bool,
    valueType: 'boolean',
    booleanDropDownOptions: {
      whenFalse: "No, don't include the Handling Units field",
      whenTrue: 'Yes, include the Handling Units field'
    },
    isActive: true,
    group: SettingGroups.HANDLING_UNITS
  },
  handlingUnitsLabel: {
    label: 'Handling Units Label',
    defaultValue: 'Handling Units',
    description: 'The display name for the unit type field. Eg: "Pallet Count", "Skid Count"',
    note: `${notePrefix} If empty, the default name will be applied`,
    inputType: SettingInputType.String,
    valueType: 'string',
    requirements: { includeHandlingUnitsFieldOnAppointmentForms: true },
    isActive: true,
    group: SettingGroups.HANDLING_UNITS
  },
  handlingUnitsHelperText: {
    label: 'Handling Units Helper Text',
    defaultValue: '',
    description:
      'Additional information to help fill in the unit type field. Eg: "Unit Type should be only numbers"',
    inputType: SettingInputType.String,
    valueType: 'string',
    requirements: { includeHandlingUnitsFieldOnAppointmentForms: true },
    isActive: true,
    group: SettingGroups.HANDLING_UNITS
  },
  isHandlingUnitsRequired: {
    label: 'Make Handling Units a required field?',
    defaultValue: false,
    description: 'Require the unit type field for all appointments being scheduled',
    inputType: SettingInputType.Bool,
    valueType: 'boolean',
    booleanDropDownOptions: {
      whenFalse: "No, don't make Handling Units a required field",
      whenTrue: 'Yes, make Handling Units a required field'
    },
    requirements: { includeHandlingUnitsFieldOnAppointmentForms: true },
    isActive: true,
    group: SettingGroups.HANDLING_UNITS
  },
  handlingUnitsMinimumThreshold: {
    label: 'Minimum Handling Units value for an appointment',
    defaultValue: 0,
    description:
      'Sets the minimum numeric value that can be entered for the Handling Units field on an appointment',
    inputType: SettingInputType.Number,
    valueType: 'number',
    minValue: 0,
    maxValue: GlobalLimits.POSTGRES_MAX_INT.value,
    requirements: { includeHandlingUnitsFieldOnAppointmentForms: true },
    isActive: true,
    group: SettingGroups.HANDLING_UNITS
  },
  handlingUnitsMaximumThreshold: {
    label: 'Maximum Handling Units value for an appointment',
    defaultValue: 0,
    description:
      'Sets the maximum numeric value that can be entered for the Handling Units field on an appointment',
    inputType: SettingInputType.Number,
    valueType: 'number',
    minValue: 0,
    maxValue: GlobalLimits.POSTGRES_MAX_INT.value,
    requirements: { includeHandlingUnitsFieldOnAppointmentForms: true },
    isActive: true,
    group: SettingGroups.HANDLING_UNITS
  },
  checkoutBolSignedRequired: {
    label: 'Check-out BOL Signed Required',
    defaultValue: false,
    description: 'Enforce BOL signing on check-out form',
    inputType: SettingInputType.Bool,
    valueType: 'boolean',
    isActive: true,
    group: SettingGroups.GATE_MANAGEMENT
  },
  checkoutBolPrintingRequired: {
    label: 'Require BOL Printing',
    defaultValue: true,
    description: 'Driver must print the BOL to complete the checkout with a physical signature.',
    inputType: SettingInputType.Bool,
    valueType: 'boolean',
    isActive: true,
    group: SettingGroups.GATE_MANAGEMENT
  },
  checkoutBolNotLoadingErrorMessage: {
    label: 'Check-out BOL Not Loading Error Message',
    defaultValue: '',
    description: 'Error message shown to drivers when the BOL fails to load during check-out',
    inputType: SettingInputType.LongString,
    valueType: 'string',
    isActive: true,
    group: SettingGroups.GATE_MANAGEMENT
  },
  checkoutIdentifier: {
    label: 'Check-out Identifier',
    defaultValue: CheckoutIdentifierAttribute.PHONE_NUMBER,
    description: 'The asset attribute the system will use to validate the check-out form',
    inputType: SettingInputType.DropDown,
    valueType: 'string',
    dropDownOptions: [
      {
        label: CheckoutIdentifierAttribute.LICENSE_PLATE,
        value: CheckoutIdentifierAttribute.LICENSE_PLATE
      },
      {
        label: CheckoutIdentifierAttribute.PHONE_NUMBER,
        value: CheckoutIdentifierAttribute.PHONE_NUMBER
      },
      {
        label: CheckoutIdentifierAttribute.ANY,
        value: CheckoutIdentifierAttribute.ANY
      }
    ],
    isActive: true,
    group: SettingGroups.GATE_MANAGEMENT
  },
  enableCheckinIdValidation: {
    label: 'Enable check-in ID validation (Persona)',
    defaultValue: false,
    description: 'Show the Persona component in the check-in flow for identity verification',
    inputType: SettingInputType.Bool,
    valueType: 'boolean',
    booleanDropDownOptions: {
      whenFalse: 'No',
      whenTrue: 'Yes'
    },
    isActive: true,
    group: SettingGroups.ID_VALIDATION
  },
  checkinIdValidationRequired: {
    label: 'Require check-in ID validation to complete check-in',
    defaultValue: false,
    description: 'Require successful Persona validation before the driver can complete check-in',
    inputType: SettingInputType.Bool,
    valueType: 'boolean',
    booleanDropDownOptions: {
      whenFalse: 'No',
      whenTrue: 'Yes'
    },
    isActive: true,
    group: SettingGroups.ID_VALIDATION
  },
  checkinIdValidationTemplateId: {
    label: 'Check-in ID validation template',
    defaultValue: getCheckinIdValidationTemplateOptions()[0]?.value ?? null,
    description: 'Persona template used for check-in identity verification',
    inputType: SettingInputType.DropDown,
    valueType: 'string',
    dropDownOptions: getCheckinIdValidationTemplateOptions(),
    isActive: true,
    group: SettingGroups.ID_VALIDATION
  },
  autoDeleteIdValidationData: {
    label: 'Automatically delete stored ID validations',
    defaultValue: false,
    description:
      'Stored ID validation records are permanently deleted once the retention period has passed',
    inputType: SettingInputType.Bool,
    valueType: 'boolean',
    isActive: true,
    group: SettingGroups.ID_VALIDATION,
    sectionLabel: 'Data Retention'
  },
  idValidationDataRetentionDays: {
    label: 'Delete stored ID validations after',
    defaultValue: DEFAULT_ID_VALIDATION_DATA_RETENTION_DAYS,
    description: 'Number of days a stored ID validation is kept before it is deleted',
    inputType: SettingInputType.Number,
    valueType: 'number',
    isActive: true,
    group: SettingGroups.ID_VALIDATION,
    minValue: ID_VALIDATION_DATA_RETENTION_DAYS_LIMITS.min,
    maxValue: ID_VALIDATION_DATA_RETENTION_DAYS_LIMITS.max
  },
  enableOverrideLimiterFunction: {
    label: 'Enable Override Limiter Function',
    defaultValue: false,
    description:
      'When the limiter is 100% reached, this feature allows only warehouse users to override and create or approve appointments and units',
    inputType: SettingInputType.Bool,
    valueType: 'boolean',
    isActive: true,
    group: SettingGroups.LIMITER
  },
  receiveUnplannedCheckinEmail: {
    label: 'Receive Unplanned Check-in Email Notifications',
    defaultValue: false,
    description:
      'Warehouse subscribers receive email notifications when an unplanned check-in occurs',
    inputType: SettingInputType.Bool,
    valueType: 'boolean',
    booleanDropDownOptions: {
      whenFalse: 'No',
      whenTrue: 'Yes'
    },
    isActive: true,
    group: SettingGroups.GATE_MANAGEMENT
  }
};

export function getTranslatedSettings(entity: keyof typeof EntitySettings): {
  [k: string]: ISettingsMetadata;
} {
  const entityKey = String(entity).toLowerCase() === 'organization' ? 'Org' : entity;
  const entitySettingKeys = Object.keys(EntitySettings[entityKey] || {});
  const translatedEntity = t(`core.global.entities.${String(entity).toLowerCase()}s`);

  return Object.fromEntries(
    entitySettingKeys.map(key => {
      const setting = Settings[key];
      const camelCaseLabel = setting.label ? camelCase(setting.label) : '';
      const labelKey = setting.label ? `core.settings.labels.${camelCaseLabel}` : undefined;
      const descriptionKey = setting.description
        ? `core.settings.descriptions.${camelCase(setting.description)}`
        : undefined;
      const placeholderKey = setting.placeholder
        ? `core.settings.placeholders.${camelCase(setting.placeholder)}`
        : undefined;
      const sectionLabelKey = setting.sectionLabel
        ? `core.settings.sectionLabels.${camelCase(setting.sectionLabel)}`
        : undefined;

      // Handle notes
      let notes;
      if (setting.note) {
        const cleanNote = setting.note.replace(notePrefix, '');
        const noteKey = `core.settings.notes.${camelCase(cleanNote)}`;
        const translatedNotesPrefix = t('core.settings.notes.notesPrefix');
        notes = `<strong>${translatedNotesPrefix}</strong> ${t(noteKey, { translatedEntity })}`;
      }

      // Translate dropdown options
      const translatedDropDownOptions = setting.dropDownOptions?.map(opt => ({
        ...opt,
        label: opt.label
          ? t(`core.settings.dropdownOptions.${camelCaseLabel}.${camelCase(opt.label)}`, {
              translatedEntity
            })
          : opt.label
      }));

      // Translate boolean dropdown options
      const translatedBooleanOptions = setting.booleanDropDownOptions
        ? {
            whenTrue: t(
              `core.settings.dropdownOptions.${camelCaseLabel}.${camelCase(
                setting.booleanDropDownOptions.whenTrue
              )}`,
              { entity: translatedEntity }
            ),
            whenFalse: t(
              `core.settings.dropdownOptions.${camelCaseLabel}.${camelCase(
                setting.booleanDropDownOptions.whenFalse
              )}`,
              { entity: translatedEntity }
            ),
            ...(setting.booleanDropDownOptions.exampleTrue && {
              exampleTrue: t(
                `core.settings.examples.${camelCaseLabel}.${camelCase(
                  setting.booleanDropDownOptions.exampleTrue
                )}`,
                { entity: translatedEntity }
              )
            }),
            ...(setting.booleanDropDownOptions.exampleFalse && {
              exampleFalse: t(
                `core.settings.examples.${camelCaseLabel}.${camelCase(
                  setting.booleanDropDownOptions.exampleFalse
                )}`,
                { entity: translatedEntity }
              )
            })
          }
        : undefined;
      return [
        key,
        {
          ...setting,
          label: labelKey ? t(labelKey, { entity: translatedEntity }) : setting.label,
          description: descriptionKey
            ? t(descriptionKey, { entity: translatedEntity })
            : setting.description,
          note: notes ?? setting.note,
          placeholder: placeholderKey
            ? t(placeholderKey, { entity: translatedEntity })
            : setting.placeholder,
          sectionLabel: sectionLabelKey
            ? t(sectionLabelKey, { entity: translatedEntity })
            : setting.sectionLabel,
          dropDownOptions: translatedDropDownOptions,
          booleanDropDownOptions: translatedBooleanOptions
        }
      ];
    })
  );
}

// TODO: Fix it so EntitySettings uses a Type, Interface or Class so we do not have a separate entity
//  to "type" our WarehouseSettings
export type WarehouseSettings = {
  appointmentStartTimeMinutes?: AppointmentStartTimeMinutes;
  // this needs to be better defined as an array or times as string
  customAppointmentStartTimes?: string[];
  isLoadTypeDurationCarrierViewHidden?: boolean;
  rescheduleByCarrierForbidden?: boolean;
  revertToRequestedOnCarrierReschedule?: boolean;
  observationEventMatchAttribute: string;
  observationEventMatchTimeWindow: number;
  gateManagementRequiresGeofencing: boolean;
  gateManagementGeofenceRadius?: number;
  gateManagementGeofenceRadiusUnit?: GeofenceRadiusUnit;
  gateManagementAdditionalInformationText: string;
  gateManagementDriverReferenceRequired?: boolean;
  gateManagementDriverReferenceValidated?: boolean;
  gateManagementCustomFirstSmsMessage?: string;
  gateManagementIncludeYardMapInFirstSms?: boolean;
  enableMilitaryTime: boolean;
  appointmentCreationStatus: string;
  statusSucceedingArrived: string;
  mutedAppointmentNotifications: string[];
  muteAppointmentNotifications: boolean;
  referenceNumbersMinimumCount: number;
  referenceNumbersMaximumCount: number;
  referenceNumberIsVisible: boolean;
  referenceNumberDisplayName: string;
  referenceNumberHelperText: string;
  referenceNumberIsRequired: boolean;
  referenceNumberIsUnique: boolean;
  referenceNumberInputMode: string;
  checkInEarlyThreshold: number;
  checkInLateThreshold: number;
  technicalContactEmailAddress: string;
  externalValidationServerErrorNotifyThrottleMinutes: number;
  overrideExternalAppointmentValidation: boolean;
  includeHandlingUnitsFieldOnAppointmentForms?: boolean;
  handlingUnitsLabel?: string;
  handlingUnitsHelperText?: string;
  isHandlingUnitsRequired?: boolean;
  handlingUnitsMinimumThreshold?: number;
  checkoutBolSignedRequired?: boolean;
  checkoutBolPrintingRequired?: boolean;
  checkoutBolNotLoadingErrorMessage?: string;
  checkoutIdentifier?: string;
  enableCheckinIdValidation?: boolean;
  checkinIdValidationRequired?: boolean;
  checkinIdValidationTemplateId?: string | null;
  autoDeleteIdValidationData?: boolean;
  idValidationDataRetentionDays?: number;
  handlingUnitsMaximumThreshold?: number;
  enableOverrideLimiterFunction?: boolean;
  receiveUnplannedCheckinEmail?: boolean;
};

export const EntitySettings: ISettingsEntityCollection = {
  Org: {
    referenceNumbersMinimumCount: Settings.referenceNumbersMinimumCount,
    referenceNumbersMaximumCount: Settings.referenceNumbersMaximumCount,
    referenceNumberIsVisible: Settings.referenceNumberIsVisible,
    referenceNumberDisplayName: Settings.referenceNumberDisplayName,
    referenceNumberHelperText: Settings.referenceNumberHelperText,
    referenceNumberIsRequired: Settings.referenceNumberIsRequired,
    referenceNumberIsUnique: Settings.referenceNumberIsUnique,
    referenceNumberInputMode: Settings.referenceNumberInputMode,
    appointmentStartTimeInterval: Settings.appointmentStartTimeInterval,
    appointmentStartTimeMinutes: Settings.appointmentStartTimeMinutes,
    customAppointmentStartTimes: Settings.customAppointmentStartTimes,
    muteAppointmentNotifications: Settings.muteAppointmentNotifications,
    mutedAppointmentNotifications: Settings.mutedAppointmentNotifications,
    customLogo: Settings.customLogo,
    appointmentCreationStatus: Settings.appointmentCreationStatus,
    revertToRequestedOnCarrierReschedule: Settings.revertToRequestedOnCarrierReschedule,
    statusSucceedingArrived: Settings.statusSucceedingArrived,
    enableMilitaryTime: Settings.enableMilitaryTime
  },
  Warehouse: {
    appointmentStartTimeMinutes: Settings.appointmentStartTimeMinutes,
    customAppointmentStartTimes: Settings.customAppointmentStartTimes,
    isLoadTypeDurationCarrierViewHidden: Settings.isLoadTypeDurationCarrierViewHidden,
    customLogo: Settings.customLogo,
    yardMap: Settings.yardMap,
    useNewGridTiles: Settings.useNewGridTiles,
    gridTileConfig: Settings.gridTileConfig,
    receiveUnplannedCheckinEmail: Settings.receiveUnplannedCheckinEmail,
    gateManagementRequiresGeofencing: Settings.gateManagementRequiresGeofencing,
    gateManagementGeofenceRadius: Settings.gateManagementGeofenceRadius,
    gateManagementGeofenceRadiusUnit: Settings.gateManagementGeofenceRadiusUnit,
    gateManagementAdditionalInformationText: Settings.gateManagementAdditionalInformationText,
    gateManagementDriverReferenceRequired: Settings.gateManagementDriverReferenceRequired,
    gateManagementDriverReferenceValidated: Settings.gateManagementDriverReferenceValidated,
    gateManagementCustomFirstSmsMessage: Settings.gateManagementCustomFirstSmsMessage,
    gateManagementIncludeYardMapInFirstSms: Settings.gateManagementIncludeYardMapInFirstSms,
    enableMilitaryTime: Settings.enableMilitaryTime,
    appointmentCreationStatus: Settings.appointmentCreationStatus,
    revertToRequestedOnCarrierReschedule: Settings.revertToRequestedOnCarrierReschedule,
    statusSucceedingArrived: Settings.statusSucceedingArrived,
    mutedAppointmentNotifications: Settings.mutedAppointmentNotifications,
    muteAppointmentNotifications: Settings.muteAppointmentNotifications,
    referenceNumbersMinimumCount: Settings.referenceNumbersMinimumCount,
    referenceNumbersMaximumCount: Settings.referenceNumbersMaximumCount,
    referenceNumberIsVisible: Settings.referenceNumberIsVisible,
    referenceNumberDisplayName: Settings.referenceNumberDisplayName,
    referenceNumberHelperText: Settings.referenceNumberHelperText,
    referenceNumberIsRequired: Settings.referenceNumberIsRequired,
    referenceNumberIsUnique: Settings.referenceNumberIsUnique,
    referenceNumberInputMode: Settings.referenceNumberInputMode,
    rescheduleByCarrierForbidden: Settings.rescheduleByCarrierForbidden,
    observationEventMatchAttribute: Settings.observationEventMatchAttribute,
    observationEventMatchTimeWindow: Settings.observationEventMatchTimeWindow,
    autoDepartAssets: Settings.autoDepartAssets,
    checkInEarlyThreshold: Settings.checkInEarlyThreshold,
    checkInLateThreshold: Settings.checkInLateThreshold,
    technicalContactEmailAddress: Settings.technicalContactEmailAddress,
    externalValidationServerErrorNotifyThrottleMinutes:
      Settings.externalValidationServerErrorNotifyThrottleMinutes,
    overrideExternalAppointmentValidation: Settings.overrideExternalAppointmentValidation,
    includeHandlingUnitsFieldOnAppointmentForms:
      Settings.includeHandlingUnitsFieldOnAppointmentForms,
    handlingUnitsLabel: Settings.handlingUnitsLabel,
    handlingUnitsHelperText: Settings.handlingUnitsHelperText,
    isHandlingUnitsRequired: Settings.isHandlingUnitsRequired,
    handlingUnitsMaximumThreshold: Settings.handlingUnitsMaximumThreshold,
    handlingUnitsMinimumThreshold: Settings.handlingUnitsMinimumThreshold,
    checkoutBolSignedRequired: Settings.checkoutBolSignedRequired,
    checkoutBolPrintingRequired: Settings.checkoutBolPrintingRequired,
    checkoutBolNotLoadingErrorMessage: Settings.checkoutBolNotLoadingErrorMessage,
    checkoutIdentifier: Settings.checkoutIdentifier,
    enableCheckinIdValidation: Settings.enableCheckinIdValidation,
    checkinIdValidationRequired: Settings.checkinIdValidationRequired,
    checkinIdValidationTemplateId: Settings.checkinIdValidationTemplateId,
    autoDeleteIdValidationData: Settings.autoDeleteIdValidationData,
    idValidationDataRetentionDays: Settings.idValidationDataRetentionDays,
    enableOverrideLimiterFunction: Settings.enableOverrideLimiterFunction
  },
  Dock: {},
  User: {},
  Company: {
    dateDisplayFormat: Settings.dateDisplayFormat,
    timeDisplayFormat: Settings.timeDisplayFormat
  },
  Appointment: {},
  LoadType: { allowCarrierDockSelection: Settings.allowCarrierDockSelection }
};

export function getTranslatedRefNumberDisplayName(displayName?: string): string {
  if (!displayName) {
    return t('core.settings.groups.referenceNumber');
  }
  return displayName.toLowerCase() === SettingGroups.REFERENCE_NUMBER.toLowerCase()
    ? t('core.settings.groups.referenceNumber')
    : displayName;
}
